/**
 * PRCelebration — Full-screen burst shown when a personal record is broken
 *
 * Renders for 2.8 seconds then auto-dismisses.
 * Confetti is pure CSS/SVG — no external deps.
 */

import { useEffect, useState } from 'react';
import type { PersonalRecord } from '@/types';

interface PRCelebrationProps {
  pr: PersonalRecord;
  onDismiss: () => void;
}

// ── Confetti particle ────────────────────────────────────────

const COLORS = ['#7c3aed','#22c55e','#f59e0b','#ef4444','#3b82f6','#f2f2f2'];
const PARTICLE_COUNT = 36;

function Confetti() {
  const particles = Array.from({ length: PARTICLE_COUNT }, (_, i) => {
    const color  = COLORS[i % COLORS.length];
    const x      = Math.random() * 100;      // vw %
    const size   = 6 + Math.random() * 8;    // px
    const delay  = Math.random() * 0.4;      // s
    const dur    = 1.2 + Math.random() * 0.8; // s
    const rotate = Math.random() * 720 - 360; // deg
    const shape  = i % 3 === 0 ? 'circle' : i % 3 === 1 ? 'rect' : 'diamond';

    return { color, x, size, delay, dur, rotate, shape, i };
  });

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map(({ color, x, size, delay, dur, shape, i }) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${x}%`,
            top: '-10px',
            width: size,
            height: shape === 'diamond' ? size : size * 0.6,
            backgroundColor: color,
            borderRadius: shape === 'circle' ? '50%' : shape === 'rect' ? '2px' : '0',
            transform: shape === 'diamond' ? 'rotate(45deg)' : 'none',
            animation: `confettiFall ${dur}s ${delay}s ease-in forwards`,
            opacity: 0,
          }}
        />
      ))}

      <style>{`
        @keyframes confettiFall {
          0%   { transform: translateY(0)   rotate(0deg);    opacity: 1; }
          100% { transform: translateY(110vh) rotate(${Math.random() * 720}deg); opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

export default function PRCelebration({ pr, onDismiss }: PRCelebrationProps) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => {
      setVisible(false);
      setTimeout(onDismiss, 400); // wait for fade out
    }, 2800);
    return () => clearTimeout(t);
  }, [onDismiss]);

  const improvement = pr.previous1RM > 0
    ? `+${Math.round((pr.new1RM - pr.previous1RM) * 10) / 10} lbs`
    : 'First logged PR!';

  return (
    <div
      className={`fixed inset-0 z-[60] flex items-center justify-center
                  transition-opacity duration-400
                  ${visible ? 'opacity-100' : 'opacity-0'}`}
      onClick={onDismiss}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/75" />

      {/* Confetti burst */}
      <Confetti />

      {/* Card */}
      <div className="relative z-10 text-center px-8 animate-slide-up">
        {/* Trophy */}
        <div className="text-7xl mb-4" style={{ filter: 'drop-shadow(0 0 20px rgba(124,58,237,0.8))' }}>
          🏆
        </div>

        <p className="text-accent text-sm font-bold uppercase tracking-[0.2em] mb-2">
          Personal Record
        </p>

        <h2 className="text-3xl font-bold text-ink tracking-tight mb-1">
          {pr.exerciseName}
        </h2>

        <p className="text-5xl font-bold font-mono text-white mb-2">
          {Math.round(pr.new1RM)} <span className="text-2xl text-ink-muted">lbs</span>
        </p>

        <p className="text-ink-muted text-sm">
          Est. 1RM · {pr.weightLbs} lbs × {pr.reps} reps
        </p>

        {pr.previous1RM > 0 && (
          <div className="mt-3 inline-flex items-center gap-2 bg-success/20 text-success
                          rounded-full px-4 py-1.5 text-sm font-semibold">
            ↑ {improvement} from {Math.round(pr.previous1RM)} lbs
          </div>
        )}

        <p className="text-ink-faint text-xs mt-6">Tap anywhere to continue</p>
      </div>
    </div>
  );
}
